import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box, Button, CircularProgress, Typography, ToggleButton,
  Card, CardContent, CardActions, Stack, Chip, Divider,
  useTheme, useMediaQuery,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { DataGrid, gridClasses, GridToolbar } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { lightGreen, red } from '@mui/material/colors';
import { useLayout } from '../../../context/LayoutContext';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import { toast } from 'react-toastify';

import { useGetShiftRequestsByShiftIdQuery } from '../../../slices/shiftRequestApiSlice';
import { useGetAgenciesQuery } from '../../../slices/agencyApiSlice';
import {
  useAssignShiftToAgencyMutation,
  useAssignShiftToUserMutation,
  useGetApprovedFilledShiftsQuery,
} from '../../../slices/shiftApiSlice';
import { useGetHospitalLocationsQuery } from '../../../slices/hospitalLocationApiSlice';
import { useUpdateShiftRequestStatusMutation } from '../../../slices/shiftRequestApiSlice';

// ─── Persistence keys ─────────────────────────────────────────────────────────
// Namespaced separately from the admin version so they don't share state
const LS_SORT   = 'mgr_shiftWardList_sortModel';
const LS_FILTER = 'mgr_shiftWardList_filterModel';
const LS_PAST   = 'mgr_shiftWardList_showPastOnly';

const loadLS = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v !== null ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
/**
 * Format a date to the user's local time.
 * Replaces valueFormatter which renders UTC in MUI DataGrid.
 */
const fmtLocal = (value) => {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  return d.toLocaleString(undefined, {
    day:    '2-digit',
    month:  '2-digit',
    year:   'numeric',
    hour:   '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

// ─── Row / card colour logic ────────────────────────────────────────────────
// Single source of truth: returns a semantic key, which is then mapped to a
// DataGrid row class (desktop) or a card accent colour (mobile).
const getRowColorKey = (row) => {
  const now = new Date();
  const end = new Date(row.endDateTime);
  if (end < now) return 'red';

  switch (row.lineStatus?.toLowerCase()) {
    case 'open':
    case 'on hold':        return 'lightOrange';
    case 'archived':
    case 'filledbystaff':
    case 'filled':
    case 'complete':       return 'green';
    case 'filledbyagency': return 'darkGreen';
    case 'cancelled':      return 'red';
    default:                return 'lightGreen';
  }
};

const CARD_ACCENT_COLORS = {
  lightGreen:  lightGreen[100],
  red:         red[100],
  lightOrange: '#ffe0b2',
  green:       '#c8e6c9',
  darkGreen:   'rgb(149, 240, 152)',
};

// ─── Styled grid ──────────────────────────────────────────────────────────────
const StyledDataGrid = styled(DataGrid)`
  & .${gridClasses.row}.lightGreenRow  { background-color: ${lightGreen[100]}; }
  & .${gridClasses.row}.redRow         { background-color: ${red[100]}; }
  & .${gridClasses.row}.lightOrangeRow { background-color: #ffe0b2; }
  & .${gridClasses.row}.greenRow       { background-color: #c8e6c9; }
  & .${gridClasses.row}.darkGreenRow   { background-color: rgb(149, 240, 152); }
`;

// ─── Component ────────────────────────────────────────────────────────────────
const ShiftWardListScreen = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const { setFluid } = useLayout();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── Preserved sort / filter / past-toggle state ───────────────────────────
  const [sortModel, setSortModel] = useState(
    () => loadLS(LS_SORT, [{ field: 'startDateTime', sort: 'asc' }])
  );
  const [filterModel, setFilterModel] = useState(
    () => loadLS(LS_FILTER, { items: [] })
  );
  const [showPastOnly, setShowPastOnly] = useState(
    () => loadLS(LS_PAST, false)
  );

  const handleSortChange = useCallback((model) => {
    setSortModel(model);
    localStorage.setItem(LS_SORT, JSON.stringify(model));
  }, []);

  const handleFilterChange = useCallback((model) => {
    setFilterModel(model);
    localStorage.setItem(LS_FILTER, JSON.stringify(model));
  }, []);

  const togglePastOnly = () => {
    setShowPastOnly((prev) => {
      const next = !prev;
      localStorage.setItem(LS_PAST, JSON.stringify(next));
      return next;
    });
  };

  // ── Data ─────────────────────────────────────────────────────────────────
  const { data: hospitalLocations = [], isLoading: loadingLocations } = useGetHospitalLocationsQuery();
  const { data: shifts, isLoading, isError, error, refetch: refetchShifts } = useGetApprovedFilledShiftsQuery();

  // ── Manager / directorate logic ───────────────────────────────────────────
  const managerDirectorate = userInfo?.directorateName || null;
  const isManager = userInfo?.isManager === true;
  const isAdmin   = userInfo?.isAdmin === true;

  // Admin-configured ward area inclusions — additional wards a manager can
  // see regardless of their own directorate. Comes through on userInfo from
  // the login/verifyOTP response (populated HospitalLocation objects).
  const managerWardInclusions = userInfo?.wardAreaInclusions || [];
  const inclusionIds = useMemo(
    () =>
      new Set(
        managerWardInclusions.map((w) =>
          typeof w === 'object' && w !== null ? w._id : w
        )
      ),
    [managerWardInclusions]
  );

  const wardToDirectorateMap = useMemo(() => {
    if (!hospitalLocations.length) return {};
    const map = {};
    hospitalLocations.forEach((loc) => {
      if (loc.wardDepartmentArea && loc.directorate) {
        map[loc.wardDepartmentArea] = loc.directorate;
      }
    });
    return map;
  }, [hospitalLocations]);

  // Ward names the manager has been explicitly granted visibility on, outside
  // of their own directorate (the admin-configured "extra assigned areas").
  const includedWardNames = useMemo(() => {
    if (!hospitalLocations.length || inclusionIds.size === 0) return [];
    return hospitalLocations
      .filter((loc) => inclusionIds.has(loc._id))
      .map((loc) => loc.wardDepartmentArea)
      .filter(Boolean);
  }, [hospitalLocations, inclusionIds]);

  // Directorate filter first, then past-shifts toggle on top
  const filteredShifts = useMemo(() => {
    if (!shifts) return [];

    let result = shifts;

    // Managers see their directorate, plus any admin-configured ward
    // inclusions outside that directorate
    if (!isAdmin && isManager && managerDirectorate) {
      result = result.filter((shift) => {
        const ward = shift.wardDepartmentArea;
        if (!ward) return false;
        return wardToDirectorateMap[ward] === managerDirectorate || includedWardNames.includes(ward);
      });
    } else if (!isAdmin && isManager && !managerDirectorate && includedWardNames.length > 0) {
      // Manager with no directorate set, but inclusions configured — show only inclusions
      result = result.filter((shift) => includedWardNames.includes(shift.wardDepartmentArea));
    }

    // Past-only toggle
    if (showPastOnly) {
      const now = new Date();
      result = result.filter((s) => new Date(s.endDateTime) < now);
    }

    return result;
  }, [shifts, isAdmin, isManager, managerDirectorate, wardToDirectorateMap, includedWardNames, showPastOnly]);

  const uniqueWards = useMemo(
    () => [...new Set(filteredShifts.map((s) => s.wardDepartmentArea).filter(Boolean))],
    [filteredShifts]
  );

  // ── Mobile-only filters (ward / staff-or-agency) ───────────────────────────
  // The desktop grid already has column filters + the GridToolbar quick
  // filter; the phone card view has neither, so we add simple dropdowns for
  // the two fields that matter most on the ward screen. These layer on top
  // of the directorate/inclusion restriction already baked into filteredShifts.
  const [mobileWardFilter, setMobileWardFilter] = useState('all');
  const [mobileStaffFilter, setMobileStaffFilter] = useState('all');

  const mobileWardOptions = useMemo(() => [...uniqueWards].sort(), [uniqueWards]);
  const mobileStaffOptions = useMemo(
    () => [...new Set(filteredShifts.map((s) => s.filledByName).filter(Boolean))].sort(),
    [filteredShifts]
  );

  const mobileFilteredShifts = useMemo(() => {
    let result = filteredShifts;
    if (mobileWardFilter !== 'all') {
      result = result.filter((s) => s.wardDepartmentArea === mobileWardFilter);
    }
    if (mobileStaffFilter !== 'all') {
      result = result.filter((s) => s.filledByName === mobileStaffFilter);
    }
    return result;
  }, [filteredShifts, mobileWardFilter, mobileStaffFilter]);

  // ── Modal / assignment state ──────────────────────────────────────────────
  const [openModal, setOpenModal]             = useState(false);
  const [selectedShift, setSelectedShift]     = useState(null);
  const [selectedShiftId, setSelectedShiftId] = useState(null);
  const [selectedFiller, setSelectedFiller]   = useState(null);
  const [isAgencyFilling, setIsAgencyFilling] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const [updateShiftRequestStatus] = useUpdateShiftRequestStatusMutation();
  const [assignShift]              = useAssignShiftToUserMutation();
  const [assignShiftToAgency]      = useAssignShiftToAgencyMutation();
  const { data: agencies }         = useGetAgenciesQuery();

  const { data: shiftRequests, refetch } = useGetShiftRequestsByShiftIdQuery(
    selectedShiftId, { skip: !selectedShiftId }
  );

  useEffect(() => {
    setFluid(true);
    return () => setFluid(false);
  }, [setFluid]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleAssignClick = (shift, filler, isAgency = false) => {
    setSelectedShift(shift);
    setSelectedFiller(filler);
    setSelectedShiftId(shift._id);
    setIsAgencyFilling(isAgency);
    setOpenConfirmModal(true);
  };

  const handleAgencyApprove = async () => {
    try {
      await assignShiftToAgency({
        shiftId:    selectedShiftId,
        agencyId:   null,
        agencyName: '',
        agencyEmail:'',
        managerId:  userInfo._id,
        lineStatus: 'Open',
      }).unwrap();
      refetch();
    } catch (err) {
      console.error('Failed to assign shift to agency:', err);
    }
  };

  const handleApprove = async () => {
    try {
      await assignShift({
        shiftId:   selectedShiftId,
        userId:    null,
        userName:  '',
        userEmail: '',
        managerId: userInfo._id,
        lineStatus:'Open',
      }).unwrap();
      refetch();
    } catch (err) {
      console.error('Failed to assign shift:', err);
    }
  };

  const handleConfirmAssignment = async () => {
    try {
      if (!selectedShift || !selectedFiller) {
        toast.error('Please select both a shift and a filler');
        return;
      }

      if (isAgencyFilling) {
        await handleAgencyApprove();
      } else {
        await handleApprove();
      }

      const approvedRequest = shiftRequests?.find((req) => req.lineStatus === 'Approved');
      if (approvedRequest) {
        await updateShiftRequestStatus({
          shiftRequestId: approvedRequest._id,
          lineStatus: 'Open',
        }).unwrap();
      }

      refetch();
      await refetchShifts();
      setOpenConfirmModal(false);
      setOpenModal(false);
      setSelectedShift(null);
      setSelectedFiller(null);
      toast.success('Shift Reset!');
    } catch (error) {
      console.error('Assignment failed:', error);
      toast.error('Failed to reset shift');
    }
  };

  // Shared "Approve" action — navigates to the edit screen, same target as
  // the desktop grid's Actions column button.
  const handleApproveNavigate = (e, shiftId) => {
    e.stopPropagation(); // prevent the card/row click from also opening the modal
    navigate(`/manager/shiftsWard/${shiftId}/edit`);
  };

  // ── Columns (desktop grid) ──────────────────────────────────────────────
  const columns = [
    {
      field: 'startDateTime',
      headerName: 'Start Time',
      width: 170,
      type: 'dateTime',                               // keeps sort working correctly
      valueGetter: (params) => new Date(params.row.startDateTime),
      renderCell:  (params) => fmtLocal(params.value), // ← local time, not UTC
    },
    {
      field: 'endDateTime',
      headerName: 'End Time',
      width: 170,
      type: 'dateTime',
      valueGetter: (params) => new Date(params.row.endDateTime),
      renderCell:  (params) => fmtLocal(params.value), // ← local time, not UTC
    },
    { field: 'wardDepartmentArea',  headerName: 'Ward/Dept Area',      width: 150 },
    { field: 'categoryOfStaff',     headerName: 'Staff Category',       width: 150 },
    { field: 'lineStatus',          headerName: 'Line Status',          width: 120 },
    { field: 'filledByName',        headerName: 'Filled By',            width: 120 },
    { field: 'specialRequirements', headerName: 'Special Requirements', width: 150 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable:   false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={(e) => handleApproveNavigate(e, params.row._id)}
        >
          Approve
        </Button>
      ),
    },
  ];

  // ── Row colours (desktop grid) ───────────────────────────────────────────
  const getRowClassName = (params) => {
    switch (getRowColorKey(params.row)) {
      case 'red':          return 'redRow';
      case 'lightOrange':  return 'lightOrangeRow';
      case 'green':        return 'greenRow';
      case 'darkGreen':    return 'darkGreenRow';
      default:              return 'lightGreenRow';
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (isLoading || loadingLocations) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
      <CircularProgress />
    </Box>
  );

  if (isError) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
      <Typography color="error">Error: {error.message}</Typography>
    </Box>
  );

  return (
    <Box sx={{ height: isMobile ? 'auto' : 'calc(100vh - 100px)', width: '100%', p: 2 }}>

      {/* ── Header ── */}
      <Box sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'flex-start',
        gap: isMobile ? 1.5 : 0,
        mb: 2,
      }}>
        <Box>
          <Typography variant={isMobile ? 'h6' : 'h4'}>Manager: Ward — Approve and update worked shifts</Typography>
          {isManager && managerDirectorate && (
            <Typography variant="subtitle1" color="primary" sx={{ mt: 0.5 }}>
              Viewing shifts for directorate: {managerDirectorate} ({uniqueWards.length} wards)
              {inclusionIds.size > 0 && (
                <Typography component="span" variant="body2" color="success.main" sx={{ ml: 1.5, fontWeight: 600 }}>
                  + {inclusionIds.size} additional ward{inclusionIds.size > 1 ? 's' : ''} included
                </Typography>
              )}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Past-shifts toggle */}
          <ToggleButton
            value="pastOnly"
            selected={showPastOnly}
            onChange={togglePastOnly}
            size="small"
            color="warning"
            sx={{ textTransform: 'none' }}
          >
            <HistoryIcon sx={{ mr: 0.5 }} fontSize="small" />
            {showPastOnly ? 'Past shifts only' : 'All shifts'}
          </ToggleButton>

          <Button variant="contained" color="primary">
            Create Shift
          </Button>
        </Box>

        {/* Mobile-only quick filters — ward + staff/agency */}
        {isMobile && (
          <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Ward</InputLabel>
              <Select
                value={mobileWardFilter}
                label="Filter by Ward"
                onChange={(e) => setMobileWardFilter(e.target.value)}
              >
                <MenuItem value="all"><em>All Wards</em></MenuItem>
                {mobileWardOptions.map((ward) => (
                  <MenuItem key={ward} value={ward}>{ward}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Filter by Staff/Agency</InputLabel>
              <Select
                value={mobileStaffFilter}
                label="Filter by Staff/Agency"
                onChange={(e) => setMobileStaffFilter(e.target.value)}
              >
                <MenuItem value="all"><em>All Staff/Agency</em></MenuItem>
                {mobileStaffOptions.map((name) => (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>

      {isMobile ? (
        /* ── Mobile card list ── */
        <Stack spacing={1.5}>
          {mobileFilteredShifts.length === 0 && (
            <Typography color="textSecondary" align="center" sx={{ mt: 4 }}>
              No shifts to show.
            </Typography>
          )}
          {mobileFilteredShifts.map((shift) => {
            const accentColor = CARD_ACCENT_COLORS[getRowColorKey(shift)];
            return (
              <Card
                key={shift._id}
                elevation={2}
                sx={{
                  borderLeft: `6px solid ${accentColor}`,
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setSelectedShift(shift);
                  setSelectedShiftId(shift._id);
                  setOpenModal(true);
                }}
              >
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {shift.wardDepartmentArea}
                    </Typography>
                    <Chip label={shift.lineStatus} size="small" />
                  </Box>

                  <Typography variant="body2" color="textSecondary">
                    <strong>Start:</strong> {fmtLocal(shift.startDateTime)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>End:</strong> {fmtLocal(shift.endDateTime)}
                  </Typography>

                  <Divider sx={{ my: 1 }} />

                  <Typography variant="body2">
                    <strong>Staff Category:</strong> {shift.categoryOfStaff || '—'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Filled By:</strong> {shift.filledByName || 'Unassigned'}
                  </Typography>
                  {shift.specialRequirements && (
                    <Typography variant="body2">
                      <strong>Special Requirements:</strong> {shift.specialRequirements}
                    </Typography>
                  )}
                  {shift.wardDepartmentArea && wardToDirectorateMap[shift.wardDepartmentArea] && (
                    <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                      <strong>Directorate:</strong> {wardToDirectorateMap[shift.wardDepartmentArea]}
                    </Typography>
                  )}
                </CardContent>

                <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 1.5 }}>
                  <Button
                    variant="contained"
                    size="small"
                    fullWidth
                    onClick={(e) => handleApproveNavigate(e, shift._id)}
                  >
                    Approve
                  </Button>
                </CardActions>
              </Card>
            );
          })}
        </Stack>
      ) : (
        /* ── Desktop grid ── */
        <StyledDataGrid
          density="compact"
          rows={filteredShifts}
          columns={columns}
          getRowId={(row) => row._id}
          getRowClassName={getRowClassName}

          // ── Controlled sort — persisted ──
          sortModel={sortModel}
          onSortModelChange={handleSortChange}

          // ── Controlled filter — persisted ──
          filterModel={filterModel}
          onFilterModelChange={handleFilterChange}

          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          slots={{ toolbar: GridToolbar }}
          slotProps={{ toolbar: { showQuickFilter: true } }}
          disableSelectionOnClick
          onRowClick={(params) => {
            setSelectedShift(params.row);
            setSelectedShiftId(params.row._id);
            setOpenModal(true);
          }}
        />
      )}

      {/* ── Detail modal ── */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Shift Details
          <IconButton
            aria-label="close"
            onClick={() => setOpenModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {selectedShift ? (
            <Box>
              <Typography variant="body1"><strong>Start:</strong>                {fmtLocal(selectedShift.startDateTime)}</Typography>
              <Typography variant="body1"><strong>End:</strong>                  {fmtLocal(selectedShift.endDateTime)}</Typography>
              <Typography variant="body1"><strong>Ward/Dept:</strong>            {selectedShift.wardDepartmentArea}</Typography>
              <Typography variant="body1"><strong>Category of Staff:</strong>    {selectedShift.categoryOfStaff}</Typography>
              <Typography variant="body1"><strong>Line Status:</strong>          {selectedShift.lineStatus}</Typography>
              <Typography variant="body1"><strong>Filled By:</strong>            {selectedShift.filledByName || 'Unassigned'}</Typography>
              <Typography variant="body1"><strong>Special Requirements:</strong> {selectedShift.specialRequirements || 'None'}</Typography>
              {selectedShift.wardDepartmentArea && wardToDirectorateMap[selectedShift.wardDepartmentArea] && (
                <Typography variant="body1" color="primary" sx={{ mt: 1 }}>
                  <strong>Directorate:</strong> {wardToDirectorateMap[selectedShift.wardDepartmentArea]}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography>No shift selected.</Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button onClick={handleConfirmAssignment} color="primary" variant="contained">
            Confirm Shift Reset
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ShiftWardListScreen;